import { useForm } from 'react-hook-form';
import { Button, Field, Input } from '../../components/ui';
import { ApiError } from '../../lib/api';
import { useChangePassword, useLogout, type AuthUser } from './api';

interface FormValues {
  currentPassword: string;
  newPassword: string;
  confirm: string;
}

// Vynucená změna hesla po prvním přihlášení (i ruční z nastavení později).
export default function ChangePasswordView({ user }: { user: AuthUser }) {
  const change = useChangePassword();
  const logout = useLogout();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormValues>({ defaultValues: { currentPassword: '', newPassword: '', confirm: '' } });

  const onSubmit = handleSubmit((v) =>
    change.mutate({ currentPassword: v.currentPassword, newPassword: v.newPassword }),
  );
  const serverError = change.error as ApiError | null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 dark:bg-zinc-950 p-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <h1 className="text-xl font-bold tracking-tight">Změna hesla</h1>
          <p className="text-sm text-zinc-500">
            Přihlášen jako {user.email}. Před prvním použitím si nastav nové heslo.
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          className="space-y-4 rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-6"
        >
          <Field label="Stávající heslo" htmlFor="currentPassword" error={errors.currentPassword?.message}>
            <Input
              id="currentPassword"
              type="password"
              autoComplete="current-password"
              {...register('currentPassword', { required: 'Zadej stávající heslo' })}
            />
          </Field>

          <Field label="Nové heslo" htmlFor="newPassword" error={errors.newPassword?.message}>
            <Input
              id="newPassword"
              type="password"
              autoComplete="new-password"
              {...register('newPassword', {
                required: 'Zadej nové heslo',
                minLength: { value: 8, message: 'Alespoň 8 znaků' },
              })}
            />
          </Field>

          <Field label="Nové heslo znovu" htmlFor="confirm" error={errors.confirm?.message}>
            <Input
              id="confirm"
              type="password"
              autoComplete="new-password"
              {...register('confirm', {
                validate: (v) => v === watch('newPassword') || 'Hesla se neshodují',
              })}
            />
          </Field>

          {serverError && (
            <p className="text-sm text-red-600 dark:text-red-400">{serverError.message}</p>
          )}

          <Button type="submit" className="w-full" disabled={change.isPending}>
            {change.isPending ? 'Ukládám…' : 'Nastavit heslo'}
          </Button>
          <button
            type="button"
            onClick={() => logout.mutate()}
            className="w-full text-center text-xs text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200"
          >
            Odhlásit se
          </button>
        </form>
      </div>
    </div>
  );
}
